import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import apiClient, { API, getErrorMessage } from "@/lib/apiClient";
import { QrCode, Printer, Copy, MonitorSmartphone, RefreshCw, Eye, EyeOff } from "lucide-react";

const WEB_ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function QrCodes() {
  const [stations, setStations] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [rotatingStation, setRotatingStation] = useState(null);
  const [showStationQr, setShowStationQr] = useState({}); // { [stationId]: bool }

  // Live rotating reception QR
  const [liveOpen, setLiveOpen] = useState(false);
  const [liveUrl, setLiveUrl] = useState("");
  const [liveSecs, setLiveSecs] = useState(60);
  const liveTimer = useRef(null);
  const countdown = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, cfg] = await Promise.all([
          apiClient.get("/admin/stations"),
          apiClient.get("/admin/waiver-settings"),
        ]);
        setStations(s.data || []);
        setSettings(cfg.data);
      } catch (e) { toast.error(getErrorMessage(e, "Load failed")); }
      finally { setLoading(false); }
    })();
  }, []);

  const token = () => localStorage.getItem("token");

  const downloadPdf = (url, filename) => {
    fetch(url, { headers: { Authorization: `Bearer ${token()}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = filename;
        a.click();
      });
  };

  const copyDisplayUrl = () => {
    if (!settings) return;
    const url = `${WEB_ORIGIN}/checkin?display_token=${settings.central_display_token}`;
    navigator.clipboard.writeText(url);
    toast.success("Reception URL copied — open on the display tablet");
  };

  // --- Live rotating reception QR: fetch a short-lived JWT and encode it into a
  //     guest-scannable URL on THIS origin; refresh just before it expires. ---
  const fetchLive = async () => {
    if (!settings) return;
    try {
      const r = await apiClient.get("/qr/central/token", { params: { display_token: settings.central_display_token } });
      setLiveUrl(`${WEB_ORIGIN}/checkin?token=${encodeURIComponent(r.data.token)}`);
      const ttl = Number(r.data.expires_in || 60);
      setLiveSecs(ttl);
    } catch (e) { toast.error(getErrorMessage(e, "Could not load live QR")); }
  };

  const stopLive = () => {
    if (liveTimer.current) clearInterval(liveTimer.current);
    if (countdown.current) clearInterval(countdown.current);
    liveTimer.current = null; countdown.current = null;
  };

  const toggleLive = async () => {
    if (liveOpen) { setLiveOpen(false); stopLive(); return; }
    setLiveOpen(true);
    await fetchLive();
    stopLive();
    liveTimer.current = setInterval(fetchLive, 55000); // refresh before 60s expiry
    countdown.current = setInterval(() => setLiveSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
  };

  useEffect(() => () => stopLive(), []);

  const rotateCentral = async () => {
    if (!window.confirm("Regenerate the reception QR? Any previously printed or shared reception QR will stop working immediately.")) return;
    setRotating(true);
    try {
      const r = await apiClient.post("/admin/waiver-settings/rotate-central-token");
      setSettings(r.data);
      if (liveOpen) await fetchLive();
      toast.success("Reception QR regenerated — reprint the poster and reopen the display URL");
    } catch (e) { toast.error(getErrorMessage(e, "Rotate failed")); }
    finally { setRotating(false); }
  };

  const rotateStation = async (s) => {
    if (!window.confirm(`Regenerate the QR for "${s.name}"? The old printed QR at this station will stop working — reprint and repost it.`)) return;
    setRotatingStation(s.id);
    try {
      const r = await apiClient.post(`/admin/stations/${s.id}/rotate-qr-secret`);
      setStations((prev) => prev.map((x) => (x.id === s.id ? { ...x, qr_secret: r.data.qr_secret } : x)));
      toast.success(`${s.name} QR regenerated — reprint & repost the new code`);
    } catch (e) { toast.error(getErrorMessage(e, "Rotate failed")); }
    finally { setRotatingStation(null); }
  };

  const stationUrl = (s) => `${WEB_ORIGIN}/checkin/station/${s.id}${s.qr_secret ? `?k=${s.qr_secret}` : ""}`;

  return (
    <div className="space-y-6 max-w-5xl">
      <div>
        <h1 className="text-3xl font-bold text-white flex items-center gap-3"><QrCode className="w-8 h-8 text-pink-400" /> QR codes</h1>
        <p className="text-slate-400 mt-1">Show the live QR on a tablet, or print & post these at reception + each station.</p>
      </div>

        {settings && (
          <Card className="p-6 bg-gradient-to-br from-pink-500/10 to-violet-500/10 border border-pink-500/30">
            <div className="flex items-center gap-3 mb-2">
              <MonitorSmartphone className="w-6 h-6 text-pink-400" />
              <div>
                <h3 className="text-white font-bold text-lg">Reception check-in QR</h3>
                <p className="text-slate-400 text-sm">Show the live rotating QR here (or on the display tablet). Guests scan it with their phone to check in — it refreshes every 60 seconds.</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <Button onClick={toggleLive} className="bg-gradient-to-r from-pink-500 to-violet-500" data-testid="qr-central-live-btn">
                {liveOpen ? <><EyeOff className="w-4 h-4 mr-2" /> Hide live QR</> : <><Eye className="w-4 h-4 mr-2" /> Show live QR</>}
              </Button>
              <Button onClick={() => downloadPdf(`${API}/admin/qr-codes/central.pdf?origin=${encodeURIComponent(WEB_ORIGIN)}`, "reception-qr.pdf")}
                className="bg-white/10 hover:bg-white/15" data-testid="qr-central-pdf-btn">
                <Printer className="w-4 h-4 mr-2" /> Download poster (PDF)
              </Button>
              <Button onClick={copyDisplayUrl} className="bg-white/10 hover:bg-white/15" data-testid="qr-display-url-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy display URL
              </Button>
              <Button onClick={rotateCentral} disabled={rotating} className="bg-white/10 hover:bg-white/15 text-amber-300" data-testid="qr-central-rotate-btn">
                <RefreshCw className={`w-4 h-4 mr-2 ${rotating ? "animate-spin" : ""}`} /> Regenerate
              </Button>
            </div>

            {liveOpen && (
              <div className="mt-6 flex flex-col items-center" data-testid="qr-central-live">
                <div className="bg-white p-4 rounded-2xl">
                  {liveUrl ? <QRCodeSVG value={liveUrl} size={240} level="M" /> : <div className="w-[240px] h-[240px] grid place-items-center text-slate-500">Loading…</div>}
                </div>
                <p className="text-slate-400 text-sm mt-3">Refreshes in <b className="text-white">{liveSecs}s</b> · scan with your phone camera</p>
              </div>
            )}
          </Card>
        )}

        <Card className="p-6 bg-white/5 border border-white/10">
          <h3 className="text-white font-bold text-lg mb-4">Station QR codes</h3>
          {loading ? <div className="text-slate-400">Loading…</div> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {stations.map((s) => (
                <div key={s.id} data-testid={`qr-station-${s.id}`}
                  className="p-4 rounded-xl bg-black/30 border border-white/10 flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-white font-semibold truncate">{s.name}</div>
                      <div className="text-xs text-slate-500 uppercase">{s.type?.replace(/_/g, " ")}</div>
                      {s.qr_secret && <div className="text-[10px] text-emerald-400 mt-0.5">Secured · rotated</div>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button onClick={() => setShowStationQr((p) => ({ ...p, [s.id]: !p[s.id] }))}
                        className="bg-white/10 hover:bg-white/15" data-testid={`qr-station-show-${s.id}`}>
                        {showStationQr[s.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </Button>
                      <Button onClick={() => downloadPdf(`${API}/admin/qr-codes/station/${s.id}.pdf?origin=${encodeURIComponent(WEB_ORIGIN)}`, `qr-${s.name.replace(/\s+/g, "-")}.pdf`)}
                        className="bg-white/10 hover:bg-white/15" data-testid={`qr-station-pdf-${s.id}`}>
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button onClick={() => rotateStation(s)} disabled={rotatingStation === s.id}
                        className="bg-white/10 hover:bg-white/15 text-amber-300" data-testid={`qr-station-rotate-${s.id}`}>
                        <RefreshCw className={`w-4 h-4 ${rotatingStation === s.id ? "animate-spin" : ""}`} />
                      </Button>
                    </div>
                  </div>
                  {showStationQr[s.id] && (
                    <div className="flex justify-center bg-white rounded-xl p-3" data-testid={`qr-station-live-${s.id}`}>
                      <QRCodeSVG value={stationUrl(s)} size={180} level="M" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
  );
}
